import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Button,
	IconButton,
	Chip,
	Box,
	Divider,
	Tabs,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow
} from '@material-ui/core';
import {
	Close as CloseIcon,
	MoreVert as MoreVertIcon,
	Storage as StorageIcon,
	Memory as MemoryIcon,
	Speed as SpeedIcon,
	CloudQueue as CloudIcon,
	LocationOn as LocationIcon,
	Computer as ComputerIcon,
	CheckCircle as CheckCircleIcon,
	Error as ErrorIcon,
	Schedule as ScheduleIcon
} from '@material-ui/icons';
import { getThemeColors } from '../utils/themeUtils';
import { getLogoFilename } from 'app/main/util/provider';
import { ClusterActionMenu } from 'app/main/cluster/ClusterActionMenu';
import * as ClusterActions from 'app/main/cluster/store/actions';
import { showMessage } from 'app/store/actions/fuse';

const useStyles = makeStyles(theme => ({
	dialog: {
		'& .MuiDialog-paper': {
			borderRadius: '12px',
			maxWidth: '600px',
			width: '100%'
		}
	},
	dialogDark: {
		'& .MuiDialog-paper': {
			backgroundColor: '#1e293b',
			border: '1px solid #334155'
		}
	},
	dialogLight: {
		'& .MuiDialog-paper': {
			backgroundColor: '#ffffff',
			border: '1px solid #e2e8f0'
		}
	},
	dialogTitle: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: theme.spacing(2, 3),
		borderBottom: '1px solid'
	},
	dialogTitleDark: {
		borderBottomColor: '#334155'
	},
	dialogTitleLight: {
		borderBottomColor: '#e2e8f0'
	},
	titleSection: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2)
	},
	providerLogo: {
		width: 40,
		height: 40,
		objectFit: 'contain',
		borderRadius: '8px',
		padding: '4px',
		backgroundColor: 'rgba(255, 255, 255, 0.1)'
	},
	titleActions: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1)
	},
	dialogContent: {
		padding: theme.spacing(3)
	},
	section: {
		marginBottom: theme.spacing(3)
	},
	sectionTitle: {
		fontSize: '12px',
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: '0.5px',
		marginBottom: theme.spacing(1.5)
	},
	infoGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(2, 1fr)',
		gap: theme.spacing(2)
	},
	infoItem: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: theme.spacing(1.5)
	},
	infoIcon: {
		fontSize: '20px',
		marginTop: '2px'
	},
	infoLabel: {
		fontSize: '12px',
		fontWeight: 500,
		marginBottom: '2px'
	},
	infoValue: {
		fontSize: '14px',
		fontWeight: 600
	},
	statusChip: {
		fontWeight: 600,
		fontSize: '12px'
	},
	metricsGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, 1fr)',
		gap: theme.spacing(2)
	},
	metricCard: {
		padding: theme.spacing(2),
		borderRadius: '8px',
		textAlign: 'center'
	},
	metricCardDark: {
		backgroundColor: '#0f172a',
		border: '1px solid #334155'
	},
	metricCardLight: {
		backgroundColor: '#f8fafc',
		border: '1px solid #e2e8f0'
	},
	metricValue: {
		fontSize: '24px',
		fontWeight: 700,
		lineHeight: 1.2
	},
	metricLabel: {
		fontSize: '11px',
		fontWeight: 500,
		marginTop: '4px'
	},
	dialogActions: {
		padding: theme.spacing(2, 3),
		borderTop: '1px solid'
	},
	dialogActionsDark: {
		borderTopColor: '#334155'
	},
	dialogActionsLight: {
		borderTopColor: '#e2e8f0'
	},
	connectButton: {
		backgroundColor: '#1C75D2',
		color: '#ffffff',
		fontWeight: 600,
		padding: theme.spacing(1, 3),
		'&:hover': {
			backgroundColor: '#1565C0'
		}
	},
	locationText: {
		display: 'flex',
		flexDirection: 'column'
	},
	tabsRoot: {
		minHeight: '36px',
		marginBottom: theme.spacing(1.5)
	},
	tab: {
		minHeight: '36px',
		fontWeight: 600,
		textTransform: 'none',
		fontSize: '13px',
		padding: '6px 16px'
	},
	tableContainer: {
		maxHeight: '200px',
		overflowY: 'auto',
		borderRadius: '8px',
		border: '1px solid'
	}
}));

const ClusterInfoDialog = ({ open, onClose, cluster, themeMode = 'dark' }) => {
	const classes = useStyles();
	const history = useHistory();
	const dispatch = useDispatch();
	const colors = getThemeColors(themeMode);
	const isDark = themeMode === 'dark';

	const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
	const [metricsTab, setMetricsTab] = useState(0);

	// Get cluster details from Redux store - nodes are at clusters.nodes (not clusters.cluster.nodes)
	// The getCluster action sets state.nodes with the fetched cluster's nodes
	const nodesData = useSelector(({ clusters }) => clusters.nodes?.data || []);
	const fetchedCluster = useSelector(({ clusters }) => clusters.cluster?.cluster); // cluster.cluster, not just cluster
	const loading = useSelector(({ dialog }) => dialog?.state?.loading || false);

	// Determine if cluster is down (no metrics available)
	const isClusterDown = cluster && (
		cluster.state?.toLowerCase() !== 'available' && 
		cluster.state?.toLowerCase() !== 'deploying' && 
		cluster.state?.toLowerCase() !== 'starting'
	);

	// Fetch cluster details on open to get node information using Redux action
	useEffect(() => {
		if (open && cluster) {
			const clusterId = cluster.cluster_id || cluster._id || cluster.id || cluster.clusterId;
			if (clusterId) {
				dispatch(ClusterActions.getCluster({ id: clusterId }));
			}
		}
		// Reset tab when dialog opens
		if (open) {
			setMetricsTab(isClusterDown ? 1 : 0);
		}
	}, [open, cluster, isClusterDown, dispatch]);

	// Early return after all hooks
	if (!cluster) return null;

	const handleConnect = () => {
		const clusterId = cluster.cluster_id || cluster._id || cluster.id || cluster.clusterId;
		if (clusterId) {
			dispatch(ClusterActions.getCluster({ id: clusterId })).then(() => {
				history.push(`/clusters/${clusterId}`);
			});
		}
		onClose();
	};

	const handleActionMenuOpen = (event) => {
		setActionMenuAnchor(event.currentTarget);
	};

	const handleActionMenuClose = () => {
		setActionMenuAnchor(null);
	};

	// Handle when an action is triggered from the menu (deploy, destroy, etc.)
	const handleActionTriggered = (action, clusterName) => {
		// Close the action menu and dialog
		setActionMenuAnchor(null);
		onClose();
		
		// Show confirmation message with black background
		const actionMessages = {
			deploy: `Deploying cluster ${clusterName}...`,
			destroy: `Destroying cluster ${clusterName}...`,
			stop: `Stopping cluster ${clusterName}...`,
			start: `Starting cluster ${clusterName}...`,
			recycle: `Recycling cluster ${clusterName}...`
		};
		
		const message = actionMessages[action] || `${action} cluster ${clusterName}...`;
		dispatch(showMessage({ 
			message,
			autoHideDuration: 4000,
			anchorOrigin: {
				vertical: 'top',
				horizontal: 'center'
			}
		}));
	};

	const handleMetricsTabChange = (event, newValue) => {
		setMetricsTab(newValue);
	};

	// Get status styling
	const getStatusInfo = (state) => {
		const stateStr = (state || '').toLowerCase();
		if (stateStr === 'available') {
			return { label: 'Active', color: colors.status.active };
		}
		if (stateStr === 'deploying') {
			return { label: 'Deploying', color: colors.status.transition };
		}
		if (stateStr === 'destroying') {
			return { label: 'Destroying', color: colors.status.transition };
		}
		if (stateStr === 'starting') {
			return { label: 'Starting', color: colors.status.transition };
		}
		if (stateStr === 'stopping') {
			return { label: 'Stopping', color: colors.status.transition };
		}
		if (stateStr === 'error' || stateStr === 'failed' || stateStr === 'offline') {
			return { label: 'Error', color: colors.status.error };
		}
		return { label: 'Down', color: colors.status.inactive };
	};

	const statusInfo = getStatusInfo(cluster.state);
	const providerName = (cluster.provider || 'Unknown').toUpperCase();
	const regionDisplay = cluster.region || cluster.location?.city || 'Unknown Region';
	const locationDisplay = cluster.location?.city && cluster.location?.country 
		? `${cluster.location.city}, ${cluster.location.country}` 
		: cluster.location?.city || cluster.location?.country || 'Unknown Location';

	// Get the cluster ID we're displaying
	const displayClusterId = cluster.cluster_id || cluster._id || cluster.id || cluster.clusterId;
	
	// Check if fetched cluster matches the one we're displaying
	const fetchedClusterMatches = fetchedCluster && (
		fetchedCluster._id === displayClusterId || 
		fetchedCluster.id === displayClusterId ||
		String(fetchedCluster._id) === String(displayClusterId)
	);

	// Build nodes list - prioritize live nodes from Redux, fallback to cluster config
	let nodesList = [];
	
	if (nodesData && nodesData.length > 0) {
		// Use live node data from the scheduler (cluster is running)
		nodesList = nodesData.map((node, index) => ({
			...node,
			isHead: index === 0
		}));
	} else if (fetchedClusterMatches && fetchedCluster.data) {
		// Build node list from cluster configuration (cluster is down/inactive)
		const clusterData = fetchedCluster.data;
		
		// Add head node from server config
		if (clusterData.server) {
			nodesList.push({
				name: fetchedCluster.name || 'head-node',
				instance_id: clusterData.server.instance_id || 'N/A',
				state: { state: fetchedCluster.info?.state || 'down' },
				isHead: true,
				isConfigured: true // Flag to indicate this is from config, not live
			});
		}
		
		// Add compute nodes from node.group config
		if (clusterData.node && clusterData.node.group && Array.isArray(clusterData.node.group)) {
			clusterData.node.group.forEach((group, groupIndex) => {
				const nodeCount = group.count || 1;
				for (let i = 0; i < nodeCount; i++) {
					nodesList.push({
						name: group.group_node_prefix 
							? `${group.group_node_prefix}-compute-${groupIndex + 1}-${i + 1}`
							: `compute-${groupIndex + 1}-${i + 1}`,
						instance_id: group.instance_id || 'N/A',
						state: { state: fetchedCluster.info?.state || 'down' },
						isHead: false,
						isConfigured: true,
						nodeType: group.nodeType || 'regular'
					});
				}
			});
		}
	}

	// Prepare cluster object for action menu - use full fetched cluster if available
	const clusterForActionMenu = fetchedClusterMatches && fetchedCluster 
		? fetchedCluster  // Use complete cluster data with all fields needed for edit
		: {
			// Fallback: Build minimal cluster object from dashboard metrics
			id: displayClusterId,
			_id: displayClusterId,
			name: cluster.clusterName || cluster.name,
			provider: cluster.provider,
			owner: cluster.owner,
			info: {
				state: cluster.state,
				bursting: { type: cluster.info?.bursting?.type || 'none' }
			},
			locks: cluster.locks || {}
		};

	return (
		<>
			<Dialog
				open={open}
				onClose={onClose}
				className={`${classes.dialog} ${isDark ? classes.dialogDark : classes.dialogLight}`}
				maxWidth="sm"
				fullWidth
			>
				{/* Dialog Title */}
				<DialogTitle 
					disableTypography 
					className={`${classes.dialogTitle} ${isDark ? classes.dialogTitleDark : classes.dialogTitleLight}`}
				>
					<div className={classes.titleSection}>
						<img
							src={`assets/images/providers/${getLogoFilename(cluster.provider)}`}
							alt={`${providerName} Logo`}
							className={classes.providerLogo}
							style={{ backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)' }}
						/>
						<div>
							<Typography 
								variant="h6" 
								style={{ color: colors.text.primary, fontWeight: 600, fontSize: '18px' }}
							>
								{cluster.clusterName || cluster.name || 'Unnamed Cluster'}
							</Typography>
							<Chip
								label={statusInfo.label}
								size="small"
								className={classes.statusChip}
								style={{
									backgroundColor: `${statusInfo.color}20`,
									color: statusInfo.color,
									marginTop: '4px'
								}}
							/>
						</div>
					</div>
					<div className={classes.titleActions}>
						<IconButton
							onClick={handleActionMenuOpen}
							style={{ color: colors.text.secondary }}
							size="small"
						>
							<MoreVertIcon />
						</IconButton>
						<IconButton
							onClick={onClose}
							style={{ color: colors.text.secondary }}
							size="small"
						>
							<CloseIcon />
						</IconButton>
					</div>
				</DialogTitle>

				{/* Dialog Content */}
				<DialogContent className={classes.dialogContent} style={{ backgroundColor: isDark ? '#1e293b' : '#ffffff' }}>
					{/* Cluster Info Section - Always shown */}
					<div className={classes.section}>
						<Typography className={classes.sectionTitle} style={{ color: colors.text.tertiary }}>
							Cluster Information
						</Typography>
						<div className={classes.infoGrid}>
							<div className={classes.infoItem}>
								<CloudIcon className={classes.infoIcon} style={{ color: colors.accent.blue }} />
								<div>
									<Typography className={classes.infoLabel} style={{ color: colors.text.tertiary }}>
										Provider
									</Typography>
									<Typography className={classes.infoValue} style={{ color: colors.text.primary }}>
										{providerName}
									</Typography>
								</div>
							</div>
							<div className={classes.infoItem}>
								<LocationIcon className={classes.infoIcon} style={{ color: colors.accent.purple }} />
								<div className={classes.locationText}>
									<Typography className={classes.infoLabel} style={{ color: colors.text.tertiary }}>
										Region
									</Typography>
									<Typography className={classes.infoValue} style={{ color: colors.text.primary }}>
										{regionDisplay}
									</Typography>
									<Typography variant="caption" style={{ color: colors.text.muted, fontSize: '11px' }}>
										{locationDisplay}
									</Typography>
								</div>
							</div>
							<div className={classes.infoItem}>
								<ComputerIcon className={classes.infoIcon} style={{ color: colors.accent.green }} />
								<div>
									<Typography className={classes.infoLabel} style={{ color: colors.text.tertiary }}>
										Nodes
									</Typography>
									<Typography className={classes.infoValue} style={{ color: colors.text.primary }}>
										{cluster.nodeCount || 0} nodes
									</Typography>
								</div>
							</div>
							<div className={classes.infoItem}>
								<MemoryIcon className={classes.infoIcon} style={{ color: colors.accent.yellow }} />
								<div>
									<Typography className={classes.infoLabel} style={{ color: colors.text.tertiary }}>
										GPUs
									</Typography>
									<Typography className={classes.infoValue} style={{ color: colors.text.primary }}>
										{cluster.gpuCount || 0} GPUs
									</Typography>
								</div>
							</div>
						</div>
					</div>

					<Divider style={{ backgroundColor: colors.border.primary, margin: '16px 0' }} />

					{/* Metrics/Nodes Section with Tabs */}
					<div className={classes.section}>
						<Tabs
							value={metricsTab}
							onChange={handleMetricsTabChange}
							className={classes.tabsRoot}
							TabIndicatorProps={{
								style: { backgroundColor: colors.accent.blue }
							}}
						>
							<Tab 
								label="Metrics" 
								className={classes.tab}
								style={{ color: metricsTab === 0 ? colors.accent.blue : colors.text.secondary }}
								disabled={isClusterDown}
							/>
							<Tab 
								label="Nodes" 
								className={classes.tab}
								style={{ color: metricsTab === 1 ? colors.accent.blue : colors.text.secondary }}
							/>
						</Tabs>

						{/* Metrics Tab Content */}
						{metricsTab === 0 && !isClusterDown && (
							<div className={classes.metricsGrid}>
								<div className={`${classes.metricCard} ${isDark ? classes.metricCardDark : classes.metricCardLight}`}>
									<SpeedIcon style={{ color: colors.accent.blue, fontSize: '28px', marginBottom: '8px' }} />
									<Typography className={classes.metricValue} style={{ color: colors.accent.blue }}>
										{(cluster.cpuUtilization || 0).toFixed(1)}%
									</Typography>
									<Typography className={classes.metricLabel} style={{ color: colors.text.tertiary }}>
										CPU Usage
									</Typography>
								</div>
								<div className={`${classes.metricCard} ${isDark ? classes.metricCardDark : classes.metricCardLight}`}>
									<MemoryIcon style={{ color: colors.accent.green, fontSize: '28px', marginBottom: '8px' }} />
									<Typography className={classes.metricValue} style={{ color: colors.accent.green }}>
										{(cluster.gpuUtilization || 0).toFixed(1)}%
									</Typography>
									<Typography className={classes.metricLabel} style={{ color: colors.text.tertiary }}>
										GPU Usage
									</Typography>
								</div>
								<div className={`${classes.metricCard} ${isDark ? classes.metricCardDark : classes.metricCardLight}`}>
									<StorageIcon style={{ color: colors.accent.purple, fontSize: '28px', marginBottom: '8px' }} />
									<Typography className={classes.metricValue} style={{ color: colors.accent.purple }}>
										{(cluster.memoryUtilization || 0).toFixed(1)}%
									</Typography>
									<Typography className={classes.metricLabel} style={{ color: colors.text.tertiary }}>
										Memory
									</Typography>
								</div>
							</div>
						)}

						{/* Nodes Tab Content */}
						{metricsTab === 1 && (
							<>
								{/* Show note if displaying configured nodes (cluster is down) */}
								{nodesList.length > 0 && nodesList[0].isConfigured && (
									<Typography 
										variant="caption" 
										style={{ 
											color: colors.text.muted, 
											display: 'block', 
											marginBottom: '8px',
											fontStyle: 'italic'
										}}
									>
										Showing configured nodes (cluster is not running)
									</Typography>
								)}
								<TableContainer 
									className={classes.tableContainer}
									style={{ borderColor: colors.border.primary }}
								>
								<Table stickyHeader size="small">
									<TableHead>
										<TableRow>
											<TableCell style={{ 
												color: colors.text.primary, 
												fontWeight: 600, 
												backgroundColor: isDark ? '#0f172a' : '#f8fafc',
												borderBottom: `1px solid ${colors.border.primary}`
											}}>
												Node Name
											</TableCell>
											<TableCell style={{ 
												color: colors.text.primary, 
												fontWeight: 600, 
												backgroundColor: isDark ? '#0f172a' : '#f8fafc',
												borderBottom: `1px solid ${colors.border.primary}`
											}}>
												Instance Type
											</TableCell>
											<TableCell style={{ 
												color: colors.text.primary, 
												fontWeight: 600, 
												backgroundColor: isDark ? '#0f172a' : '#f8fafc',
												borderBottom: `1px solid ${colors.border.primary}`
											}}>
												State
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{nodesList.length > 0 ? (
											nodesList.map((node, index) => {
												// Get state from node.state object or string
												const stateRaw = typeof node.state === 'object' 
													? node.state.state 
													: (node.state || 'Unknown');
												// Capitalize first letter
												const stateStr = stateRaw.charAt(0).toUpperCase() + stateRaw.slice(1).toLowerCase();
												const isActive = ['running', 'available', 'busy'].includes(stateRaw.toLowerCase());
												
												return (
													<TableRow key={node.name || index} style={{ 
														backgroundColor: node.isHead 
															? (isDark ? 'rgba(29, 78, 216, 0.1)' : 'rgba(59, 130, 246, 0.05)')
															: (isDark ? '#1e293b' : '#ffffff')
													}}>
														<TableCell style={{ 
															color: colors.text.primary,
															borderBottom: `1px solid ${colors.border.primary}`,
															fontWeight: node.isHead ? 600 : 400
														}}>
															{node.name || node.hostname || `node-${index}`}
															{node.isHead && (
																<Chip 
																	label="HEAD" 
																	size="small" 
																	style={{ 
																		marginLeft: '8px', 
																		height: '18px',
																		fontSize: '10px',
																		backgroundColor: colors.accent.blue,
																		color: '#ffffff',
																		fontWeight: 600
																	}} 
																/>
															)}
														</TableCell>
														<TableCell style={{ 
															color: colors.text.secondary,
															borderBottom: `1px solid ${colors.border.primary}`
														}}>
															{node.instance_id || node.instanceType || node.shape || node.type || 'N/A'}
														</TableCell>
														<TableCell style={{ borderBottom: `1px solid ${colors.border.primary}` }}>
															<Chip
																label={stateStr === 'Running' ? 'Busy' : stateStr}
																size="small"
																style={{
																	backgroundColor: isActive
																		? `${colors.status.active}20`
																		: `${colors.status.inactive}20`,
																	color: isActive
																		? colors.status.active
																		: colors.status.inactive,
																	fontSize: '11px',
																	fontWeight: 600
																}}
															/>
														</TableCell>
													</TableRow>
												);
											})
										) : (
											<TableRow>
												<TableCell 
													colSpan={3} 
													align="center" 
													style={{ 
														color: colors.text.muted,
														borderBottom: 'none',
														padding: '24px'
													}}
												>
													{loading 
														? 'Loading node information...' 
														: isClusterDown 
															? `Cluster is ${cluster.state || 'inactive'}. Node details are available when the cluster is running.`
															: 'No node information available'
													}
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</TableContainer>
							</>
						)}
					</div>

					{/* Additional Info */}
					{cluster.lastMetricUpdate && (
						<Box style={{ marginTop: '8px' }}>
							<Typography variant="caption" style={{ color: colors.text.muted, fontSize: '11px' }}>
								Last updated: {new Date(cluster.lastMetricUpdate).toLocaleString()}
							</Typography>
						</Box>
					)}
				</DialogContent>

				{/* Dialog Actions */}
				<DialogActions 
					className={`${classes.dialogActions} ${isDark ? classes.dialogActionsDark : classes.dialogActionsLight}`}
					style={{ backgroundColor: isDark ? '#1e293b' : '#ffffff' }}
				>
					<Button
						variant="contained"
						className={classes.connectButton}
						onClick={handleConnect}
						disabled={cluster.state?.toLowerCase() !== 'available'}
					>
						Connect
					</Button>
				</DialogActions>
			</Dialog>

			{/* Cluster Action Menu */}
			<ClusterActionMenu
				selectedMenu={actionMenuAnchor}
				setSelectedMenu={setActionMenuAnchor}
				onClose={handleActionMenuClose}
				onActionTriggered={handleActionTriggered}
				cluster={clusterForActionMenu}
				history={history}
				darkTheme={isDark}
				preventNavigation={true}
			/>
		</>
	);
};

export default ClusterInfoDialog;
